import { useState, useMemo, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useProducts } from "../context/ProductContext";
import { apiGetCategories } from "../services/api";
import ProductCard from "../components/ProductCard";
import { FiFilter, FiX, FiChevronDown, FiChevronUp } from "react-icons/fi";
import "./Shop.css";

const SORT_OPTIONS = [
  { value: "default",    label: "Default" },
  { value: "price-asc",  label: "Price: Low to High" },
  { value: "price-desc", label: "Price: High to Low" },
  { value: "rating",     label: "Top Rated" },
  { value: "newest",     label: "Newest" },
];



export default function Shop() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [sort, setSort] = useState("default");
  const [priceRange, setPriceRange] = useState([0, 10000]);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [openSections, setOpenSections] = useState({ category: true });
  const [dbCategories, setDbCategories] = useState([]);

  const { products, loading } = useProducts();
  const activeCategory = searchParams.get("category") || "all";
  const searchQuery   = searchParams.get("search")   || "";

  useEffect(() => {
    apiGetCategories().then(data => {
      setDbCategories(Array.isArray(data) ? data.filter(c => c.slug !== "all") : []);
    });
  }, []);

  const toggleSection = (key) =>
    setOpenSections(prev => ({ ...prev, [key]: !prev[key] }));

  const filtered = useMemo(() => {
    let list = [...products];
    if (activeCategory !== "all" && activeCategory !== "sale") list = list.filter(p => p.category === activeCategory);
    if (activeCategory === "sale") list = list.filter(p => p.originalPrice);
    if (searchQuery) list = list.filter(p => p.name.toLowerCase().includes(searchQuery.toLowerCase()));
    list = list.filter(p => p.price >= priceRange[0] && p.price <= priceRange[1]);
    if (sort === "price-asc")       list.sort((a, b) => a.price - b.price);
    else if (sort === "price-desc") list.sort((a, b) => b.price - a.price);
    else if (sort === "rating")     list.sort((a, b) => b.rating - a.rating);
    return list;
  }, [products, activeCategory, searchQuery, priceRange, sort]);

  const setCategory = (slug) => {
    setSearchParams(slug === "all" ? {} : { category: slug });
    setSidebarOpen(false);
  };

  const pageTitle = searchQuery
    ? `Results for "${searchQuery}"`
    : activeCategory === "all" ? "All Products"
    : activeCategory === "sale" ? "Sale"
    : activeCategory.replace(/-/g, " ").replace(/\b\w/g, c => c.toUpperCase());

  return (
    <div className="shop-page">
      <div className="shop-header">
        <h1>{pageTitle}</h1>
        <p>{filtered.length} products found</p>
      </div>

      <div className="shop-layout">
        <button className="filter-toggle" onClick={() => setSidebarOpen(true)}>
          <FiFilter /> Filters
        </button>

        <aside className={`sidebar ${sidebarOpen ? "open" : ""}`}>
          <div className="sidebar-header">
            <h3>Filters</h3>
            <button onClick={() => setSidebarOpen(false)}><FiX /></button>
          </div>

          {/* Price Range */}
          <div className="filter-group">
            <div className="filter-group-head" onClick={() => toggleSection("price")}>
              <h4>Price Range</h4>
              {openSections["price"] ? <FiChevronUp /> : <FiChevronDown />}
            </div>
            {openSections["price"] && (
              <div className="filter-group-body">
                <p className="price-label">Rs. {priceRange[0].toLocaleString()} – Rs. {priceRange[1].toLocaleString()}</p>
                <input type="range" min="0" max="10000" step="100" value={priceRange[1]}
                  onChange={e => setPriceRange([priceRange[0], +e.target.value])} />
              </div>
            )}
          </div>

          {/* Categories filter */}
          <div className="filter-group">
            <div className="filter-group-head" onClick={() => toggleSection("category")}>
              <h4>Categories</h4>
              {openSections["category"] ? <FiChevronUp /> : <FiChevronDown />}
            </div>
            {openSections["category"] && (
              <div className="filter-group-body">
                <button
                  className={`filter-btn ${activeCategory === "all" ? "active" : ""}`}
                  onClick={() => setCategory("all")}>All</button>
                {dbCategories.map(cat => (
                  <button key={cat.slug}
                    className={`filter-btn ${activeCategory === cat.slug ? "active" : ""}`}
                    onClick={() => setCategory(cat.slug)}>
                    {cat.name}
                  </button>
                ))}
                <button
                  className={`filter-btn ${activeCategory === "sale" ? "active" : ""}`}
                  onClick={() => setCategory("sale")}>🏷️ Sale</button>
              </div>
            )}
          </div>
        </aside>

        {sidebarOpen && <div className="sidebar-overlay" onClick={() => setSidebarOpen(false)} />}

        <div className="shop-main">
          <div className="shop-toolbar">
            <span>{filtered.length} items</span>
            <select value={sort} onChange={e => setSort(e.target.value)}>
              {SORT_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          {loading
            ? <div className="no-results">Loading products...</div>
            : filtered.length === 0
            ? <div className="no-results">😕 No products found. Try a different filter.</div>
            : <div className="products-grid">
                {filtered.map(p => <ProductCard key={p.id} product={p} />)}
              </div>
          }
        </div>
      </div>
    </div>
  );
}
